import {
  Badge,
  Button,
  Card,
  CellText,
  DataTable,
  EmptyState,
  FormField,
  Icon,
  ListToolbar,
  Select,
  Text,
  type DataTableColumn,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageActions } from '../../../app/PageActions';
import { useRowMenu } from '../../../app/rowActions';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useFacetTexts } from '../../../lib/facets';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useResetUrlState, useUrlState } from '../../../lib/useUrlState';
import { useSession } from '../../identity/session';
import { useColonies, useLocations, type ColonyListItem, type StatusFilter } from '../api';
import { ColonyFormDialog } from '../components/ColonyFormDialog';
import { FeedingDialog } from '../components/FeedingDialog';
import { LocationFormDrawer } from '../components/LocationFormDrawer';
import { MiteCountDialog } from '../components/MiteCountDialog';
import { MoveDialog } from '../components/MoveDialog';
import { NoteDialog } from '../components/NoteDialog';
import { StatusDialog } from '../components/StatusDialog';
import { StatusBadge } from '../components/StatusBadge';
import { TreatmentDialog } from '../components/TreatmentDialog';
import { daysSince, formatAge, formatAmount, formatInfestation } from '../format';

type Bulk = 'treatment' | 'feeding' | 'move' | null;
/** A quick action from a row's `…` menu (#197). */
type RowAction = {
  kind: 'treatment' | 'feeding' | 'mite' | 'note' | 'move' | 'status';
  colony: ColonyListItem;
};
const STATUSES = ['active', 'inactive', 'all'] as const;
const FILTER_KEYS = ['q', 'standort', 'status'] as const;

/** Colony list with search, filters and bulk actions (VOL-002, VOL-007). */
export function ColoniesPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [status, setStatus] = useUrlState<StatusFilter>('status', 'active', STATUSES);
  const [locationId, setLocationId] = useUrlState('standort', '');
  const [q, setQ] = useUrlState('q', '');
  const resetFilters = useResetUrlState(FILTER_KEYS);
  const facetTexts = useFacetTexts(resetFilters);
  const columnSort = useColumnSort();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<Bulk>(null);
  const [action, setAction] = useState<RowAction | null>(null);
  const rowMenu = useRowMenu();
  const [creating, setCreating] = useState(false);
  const [creatingLocation, setCreatingLocation] = useState(false);
  const colonies = useColonies({
    status,
    location_id: locationId || undefined,
    q: q.trim() || undefined,
  });
  const locations = useLocations(false, can('locations.read'));
  const rows = colonies.data ?? [];
  const selectedIds = rows.filter((r) => selected.has(r.id)).map((r) => r.id);
  const canBulk = can('treatments.write') || can('feedings.write') || can('locations.write');
  const sharedLocation = rows
    .filter((r) => selected.has(r.id))
    .map((r) => r.location.id)
    .reduce<string | undefined | null>(
      (acc, id) => (acc === undefined ? id : acc === id ? acc : null),
      undefined,
    );
  const noLocations = locations.data?.length === 0;
  const filtered = status !== 'active' || locationId !== '' || q.trim() !== '';
  const locationName = locations.data?.find((l) => l.id === locationId)?.name;
  const create = () => (noLocations ? setCreatingLocation(true) : setCreating(true));
  const createLabel = noLocations ? t('apiary.locations.new') : t('apiary.colonies.new');

  const columns: DataTableColumn<ColonyListItem>[] = [
    {
      key: 'name',
      header: t('apiary.colonies.columns.name'),
      primary: true,
      cell: (c) => c.name,
      sortValue: (c) => c.name,
    },
    {
      key: 'location',
      header: t('apiary.colonies.columns.location'),
      cell: (c) => c.location.name,
      sortValue: (c) => c.location.name,
    },
    {
      key: 'age',
      header: t('apiary.colonies.columns.age'),
      cell: (c) => formatAge(c.origin_date, c.origin_precision),
      // Sorted by age, not by date: the first click (▼) shows the oldest (#188).
      sortValue: (c) => daysSince(c.origin_date),
    },
    ...(status !== 'active'
      ? [
          {
            key: 'status',
            header: t('apiary.colonies.columns.status'),
            mobile: 'trailing' as const,
            cell: (c: ColonyListItem) => <StatusBadge status={c.status} />,
            sortValue: (c: ColonyListItem) => t(`apiary.status.${c.status}`),
          },
        ]
      : []),
    ...(can('treatments.read')
      ? [
          {
            key: 'treatment',
            header: t('apiary.colonies.columns.lastTreatment'),
            mobile: 'hidden' as const,
            cell: (c: ColonyListItem) =>
              c.last_treatment ? (
                <CellText
                  main={c.last_treatment.method_name}
                  sub={
                    c.last_treatment.running ? (
                      <Badge tone="warning">
                        {t('apiary.colonies.running', {
                          count: daysSince(c.last_treatment.started_on),
                        })}
                      </Badge>
                    ) : (
                      formatDate(c.last_treatment.started_on)
                    )
                  }
                />
              ) : (
                t('apiary.colonies.never')
              ),
            sortValue: (c: ColonyListItem) => c.last_treatment?.started_on,
          },
          {
            key: 'mites',
            header: t('apiary.colonies.columns.miteCount'),
            mobile: 'hidden' as const,
            cell: (c: ColonyListItem) =>
              c.last_mite_count ? (
                <CellText
                  main={formatInfestation(c.last_mite_count.infestation)}
                  sub={formatDate(c.last_mite_count.counted_on)}
                />
              ) : (
                t('apiary.colonies.never')
              ),
            sortValue: (c: ColonyListItem) => c.last_mite_count?.infestation.value,
          },
        ]
      : []),
    ...(can('feedings.read')
      ? [
          {
            key: 'feeding',
            header: t('apiary.colonies.columns.lastFeeding'),
            mobile: 'hidden' as const,
            cell: (c: ColonyListItem) =>
              c.last_feeding ? (
                <CellText
                  main={`${formatAmount(c.last_feeding.amount)} ${c.last_feeding.unit} ${c.last_feeding.feed_type_name}`}
                  sub={formatDate(c.last_feeding.fed_on)}
                />
              ) : (
                t('apiary.colonies.never')
              ),
            sortValue: (c: ColonyListItem) => c.last_feeding?.fed_on,
          },
        ]
      : []),
  ];

  const empty = !colonies.isPending && rows.length === 0 && !filtered;

  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <>
      {can('colonies.write') && (
        <PageActions>
          <Button iconStart={<Icon name="plus" size="sm" />} onClick={create}>
            {createLabel}
          </Button>
        </PageActions>
      )}

      {!empty && (
        <ListToolbar
          sort={sortMenu}
          search={{
            value: q,
            onChange: setQ,
            label: t('common.search'),
            clearLabel: t('common.clear'),
          }}
          scope={{
            label: t('apiary.statusFilter.label'),
            value: status,
            onChange: (v) => {
              setStatus(v as StatusFilter);
              setSelected(new Set());
            },
            options: [
              { value: 'active', label: t('apiary.statusFilter.active') },
              { value: 'inactive', label: t('apiary.statusFilter.inactive') },
              { value: 'all', label: t('apiary.statusFilter.all') },
            ],
          }}
          facets={{
            ...facetTexts,
            items:
              can('locations.read') && (locations.data?.length ?? 0) > 1
                ? [
                    {
                      type: 'select',
                      key: 'location',
                      label: t('apiary.filters.location'),
                      value: locationId,
                      options: (locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
                      onChange: (v) => {
                        setLocationId(v);
                        setSelected(new Set());
                      },
                    },
                  ]
                : [],
          }}
          {...(can('locations.read') && (locations.data?.length ?? 0) > 1
            ? {
                filters: {
                  label: t('common.filter'),
                  activeCount: locationId ? 1 : 0,
                  content: (
                    <FormField label={t('apiary.colonies.locationFilter')}>
                      {(p) => (
                        <Select
                          {...p}
                          value={locationId}
                          onChange={(e) => {
                            setLocationId(e.target.value);
                            setSelected(new Set());
                          }}
                          options={[
                            { value: '', label: t('apiary.colonies.allLocations') },
                            ...(locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
                          ]}
                        />
                      )}
                    </FormField>
                  ),
                },
              }
            : {})}
          chips={{
            chips: locationName
              ? [
                  {
                    key: 'location',
                    label: t('apiary.colonies.locationChip', { name: locationName }),
                    onRemove: () => setLocationId(''),
                  },
                ]
              : [],
            removeLabel: (label) => t('common.removeFilter', { label }),
            resetLabel: t('common.resetAll'),
            onReset: resetFilters,
          }}
          {...(selectedIds.length > 0
            ? {
                selection: {
                  label: t('common.selected', { count: selectedIds.length }),
                  clearLabel: t('common.clearSelection'),
                  onClear: () => setSelected(new Set()),
                  actions: (
                    <>
                      {can('treatments.write') && (
                        <Button size="sm" variant="secondary" onClick={() => setBulk('treatment')}>
                          {t('apiary.quick.treatment')}
                        </Button>
                      )}
                      {can('feedings.write') && (
                        <Button size="sm" variant="secondary" onClick={() => setBulk('feeding')}>
                          {t('apiary.quick.feeding')}
                        </Button>
                      )}
                      {can('locations.write') && (
                        <Button size="sm" variant="secondary" onClick={() => setBulk('move')}>
                          {t('apiary.colony.move')}
                        </Button>
                      )}
                    </>
                  ),
                },
              }
            : {})}
        />
      )}

      <Card padding="none">
        {colonies.error ? (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-4)' }}>
            {errorMessage(colonies.error)}
          </Text>
        ) : (
          <DataTable
            caption={t('apiary.colonies.caption')}
            columns={columns}
            rows={rows}
            rowKey={(c) => c.id}
            rowHref={(c) => `/voelker/${c.id}`}
            {...columnSort}
            loading={colonies.isPending}
            rowActions={rowMenu.table<ColonyListItem>(
              (c) => c.name,
              (c) => {
                const act = (kind: RowAction['kind']) => () => setAction({ kind, colony: c });
                const active = c.status === 'active';
                return [
                  active &&
                    can('treatments.write') && {
                      key: 'treatment',
                      label: t('apiary.treatments.record'),
                      onSelect: act('treatment'),
                    },
                  active &&
                    can('feedings.write') && {
                      key: 'feeding',
                      label: t('apiary.feedings.record'),
                      onSelect: act('feeding'),
                    },
                  active &&
                    can('treatments.write') && {
                      key: 'mite',
                      label: t('apiary.miteCounts.record'),
                      onSelect: act('mite'),
                    },
                  can('colonies.write') && {
                    key: 'note',
                    label: t('apiary.note.add'),
                    onSelect: act('note'),
                  },
                  active &&
                    can('locations.write') && {
                      key: 'move',
                      label: t('apiary.colony.move'),
                      separated: true,
                      onSelect: act('move'),
                    },
                  can('colonies.write') && {
                    key: 'status',
                    label: t('apiary.colony.changeStatus'),
                    separated: !(active && can('locations.write')),
                    onSelect: act('status'),
                  },
                ];
              },
            )}
            {...(canBulk && status !== 'inactive'
              ? {
                  selection: {
                    selected,
                    onChange: setSelected,
                    rowLabel: (c: ColonyListItem) => t('apiary.colonies.select', { name: c.name }),
                    allLabel: t('apiary.colonies.selectAll'),
                  },
                }
              : {})}
            empty={
              filtered ? (
                <EmptyState
                  title={t('common.noResults')}
                  actions={
                    <Button variant="secondary" onClick={resetFilters}>
                      {t('common.resetFilters')}
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon="hive"
                  title={t('apiary.colonies.emptyTitle')}
                  {...(noLocations ? { description: t('apiary.colonies.noLocationText') } : {})}
                  actions={can('colonies.write') && <Button onClick={create}>{createLabel}</Button>}
                />
              )
            }
          />
        )}
      </Card>

      {creating && (
        <ColonyFormDialog
          open
          onClose={() => setCreating(false)}
          {...(locationId ? { locationId } : {})}
        />
      )}
      {creatingLocation && (
        <LocationFormDrawer
          open
          onClose={() => setCreatingLocation(false)}
          onCreated={() => setCreating(true)}
        />
      )}
      {bulk === 'treatment' && (
        <TreatmentDialog
          open
          colonyIds={selectedIds}
          onClose={() => setBulk(null)}
          onRecorded={() => setSelected(new Set())}
        />
      )}
      {bulk === 'feeding' && (
        <FeedingDialog
          open
          colonyIds={selectedIds}
          onClose={() => setBulk(null)}
          onRecorded={() => setSelected(new Set())}
        />
      )}
      {action?.kind === 'treatment' && (
        <TreatmentDialog open colonyIds={[action.colony.id]} onClose={() => setAction(null)} />
      )}
      {action?.kind === 'feeding' && (
        <FeedingDialog open colonyIds={[action.colony.id]} onClose={() => setAction(null)} />
      )}
      {action?.kind === 'mite' && (
        <MiteCountDialog open colonyId={action.colony.id} onClose={() => setAction(null)} />
      )}
      {action?.kind === 'note' && (
        <NoteDialog open colonyId={action.colony.id} onClose={() => setAction(null)} />
      )}
      {action?.kind === 'move' && (
        <MoveDialog
          open
          colonyIds={[action.colony.id]}
          currentLocationId={action.colony.location.id}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === 'status' && (
        <StatusDialog open colony={action.colony} onClose={() => setAction(null)} />
      )}
      {bulk === 'move' && (
        <MoveDialog
          open
          colonyIds={selectedIds}
          {...(sharedLocation ? { currentLocationId: sharedLocation } : {})}
          onClose={() => setBulk(null)}
          onMoved={() => setSelected(new Set())}
        />
      )}
    </>
  );
}
